'use client';

import { useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
import { getPostHog, initAnalytics } from './lib/posthog-client';

/**
 * Headless session -> PostHog identity bridge. Mirrors
 * `components/providers/firebase-auth-sync.tsx`: `'use client'`, an effect keyed
 * on `useSession()` status, renders `null`.
 *
 * On the first authenticated session we `identify()` the person by their user
 * id; on logout we `reset()` so the next visitor starts as a fresh anon.
 *
 * The identify branch is gated on `identifiedRef`, a per-mount ref — enough to
 * avoid re-identifying every render within a session. The reset branch must NOT
 * use that ref: `signOut()` does a hard navigation, so the component remounts
 * with the ref back to `false` and a stranded identity (kiosk / shared machine)
 * would never be cleared. Instead it reads PostHog's own persisted identity
 * state — `get_property('$user_state')`, which the SDK stores in persistence
 * (localStorage/cookie) as `'identified'` after an `identify()` call and
 * `'anonymous'` after a `reset()` — so the check survives a full-page reload.
 */
export function AnalyticsIdentify(): null {
  const { data: session, status } = useSession();
  const identifiedRef = useRef(false);

  useEffect(() => {
    initAnalytics();
    const ph = getPostHog();
    if (!ph) return;

    if (
      status === 'authenticated' &&
      session?.user?.id &&
      !identifiedRef.current
    ) {
      ph.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name,
      });
      // Set only after a live client actually received the identify call.
      identifiedRef.current = true;
      return;
    }

    if (status === 'unauthenticated' && ph.get_property('$user_state') === 'identified') {
      ph.reset();
      identifiedRef.current = false;
    }
  }, [status, session?.user?.id, session?.user?.email, session?.user?.name]);

  return null;
}
